import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAction } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export function YnabCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const handleCallback = useAction(api.ynabActions.handleCallback);

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string>('');
  const [budgetName, setBudgetName] = useState<string>('');

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setStatus('error');
      setError(searchParams.get('error_description') || 'Authorization was denied');
      return;
    }

    if (!code) {
      setStatus('error');
      setError('No authorization code received');
      return;
    }

    // Exchange code for tokens
    const redirectUri = `${window.location.origin}/findash/ynab/callback`;

    handleCallback({ code, redirectUri })
      .then((result) => {
        setStatus('success');
        setBudgetName(result.budgetName);
        // Redirect after short delay
        setTimeout(() => navigate('/settings', { replace: true }), 2000);
      })
      .catch((err) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Failed to connect YNAB');
      });
  }, [searchParams, handleCallback, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 bg-bg-primary">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card max-w-md w-full text-center"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-accent mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Connecting to YNAB...</h2>
            <p className="text-text-secondary">Please wait while we set up your connection</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="h-12 w-12 text-positive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Connected!</h2>
            <p className="text-text-secondary">
              Successfully connected to <strong>{budgetName}</strong>
            </p>
            <p className="text-text-muted text-sm mt-4">Redirecting to settings...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-negative mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Connection Failed</h2>
            <p className="text-text-secondary mb-4">{error}</p>
            <button onClick={() => navigate('/settings')} className="btn-primary">
              Back to Settings
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
